import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import { useIsFetching } from '@tanstack/react-query'
import KNAdvancedDropdown from 'components/KNAdvancedDropdown/KNAdvancedDropdown'
import { CompanyType } from 'context/authentication/Company.types'
import { useCompaniesView } from 'context/CompaniesViewContext'
import { analyticsEvent } from 'global/helpers/analytics'
import { type ReactElement, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { COMPANIES_QUERY_KEY } from './CompanyManager.service'

const CompanyFilters = (): ReactElement => {
  const { t } = useTranslation()
  const { filters, filtersDirty, setFilters, resetFilters } = useCompaniesView()
  const isFetching = useIsFetching({ queryKey: [COMPANIES_QUERY_KEY] })

  const handleResetClick = useCallback((): void => {
    resetFilters()
    analyticsEvent('polestar_cv_reset_company_filters_button_clicked')
  }, [])

  return (
    <Box sx={{ position: 'relative' }}>
      <Grid container spacing={2}>
        <Grid data-test="company-search-input" size={{ xs: 12, sm: 4 }}>
          <Autocomplete
            multiple
            freeSolo
            options={[]}
            value={filters.keywords ?? []}
            onChange={(event, newValue): void => {
              setFilters({
                keywords: newValue,
              })
            }}
            renderValue={(tagValue, getTagProps): ReactElement[] =>
              tagValue.map((option, index) => (
                // eslint-disable-next-line react/jsx-key
                <Chip {...getTagProps({ index })} variant="outlined" size="small" label={option} />
              ))
            }
            renderInput={(params): ReactElement => (
              <TextField {...params} label={t('screens.shared.company_manager.filters.search')} />
            )}
            disabled={isFetching > 0}
          />
        </Grid>
        <Grid data-test="company-type-dropdown" size={{ xs: 12, sm: 2 }}>
          <KNAdvancedDropdown
            label={t('screens.shared.company_manager.filters.types')}
            options={Object.values(CompanyType)
              .filter((type) => type !== CompanyType.Operator)
              .map((type) => ({
                value: type,
                label: t(`screens.shared.company_manager.company_types.${type}`),
              }))}
            value={filters.types ?? []}
            onChange={(newValue): void => {
              setFilters({
                types: newValue as CompanyType[],
              })
            }}
            onDelete={(value) => {
              setFilters({
                types: filters.types?.filter((type) => type !== value),
              })
            }}
            multiple
            disabled={isFetching > 0}
          />
        </Grid>
      </Grid>
      {filtersDirty && (
        <Button
          size="small"
          variant="text"
          color="primary"
          onClick={handleResetClick}
          sx={{
            padding: 0,
            minHeight: 'auto',
          }}
        >
          {t('general.reset_filters')}
        </Button>
      )}
    </Box>
  )
}

export default CompanyFilters
